import { useEffect, useMemo, useState } from "react";
import { Alert, CircularProgress } from "@mui/material";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import AppDialog from "../../../components/AppDialog/AppDialog";
import CustomButton from "../../../components/buttons/CustomButton";
import Dropdown from "../../../components/buttons/Dropdown";
import { getKeycloakUserParticipantIds } from "../../../hooks/participants/helpers";
import { useAuthRoles } from "../../../hooks/useAuthRoles";
import { usePutInternalUser } from "../../../hooks/users/usePutInternalUser";
import { buildServiceUrl } from "../../../utils/urlBuilder";
import "./AddParticipantUserDialog.css";

function getAttributeValue(attributes, key) {
  const value = attributes?.[key];

  if (Array.isArray(value)) {
    return String(value[0] ?? "").trim();
  }

  return String(value ?? "").trim();
}

function buildUserLabel(user) {
  const username = String(user?.username ?? "").trim();
  const fullName = [user?.firstName, user?.lastName]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean)
    .join(" ");
  const email = String(user?.email ?? "").trim();
  const organization = getAttributeValue(user?.attributes, "organization");
  const displayName = fullName || username;
  const details = [email, organization, username ? `@${username}` : ""]
    .filter(Boolean)
    .join(" - ");

  return details ? `${displayName} • ${details}` : displayName;
}

export default function AddParticipantUserDialog({
  open,
  onClose,
  participantId,
  participantName,
  onSuccess,
}) {
  const { t } = useTranslation();
  const { token } = useAuthRoles();
  const { mutateAsync: updateInternalUser } = usePutInternalUser();
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const participantOrganization = String(
    participantName ?? participantId ?? "",
  ).trim();

  const selectableUsers = useMemo(() => {
    return availableUsers
      .filter((user) => String(user?.id ?? "").trim())
      .filter((user) => String(user?.username ?? "").trim())
      .filter(
        (user) =>
          !getKeycloakUserParticipantIds(user).includes(
            String(participantId ?? "").trim(),
          ),
      )
      .map((user) => ({
        value: String(user.id),
        label: buildUserLabel(user),
        user,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [availableUsers, participantId]);

  const userOptions = useMemo(
    () =>
      selectableUsers.map((userOption) => ({
        value: userOption.value,
        label: userOption.label,
      })),
    [selectableUsers],
  );

  useEffect(() => {
    if (!open || !token) return;

    let cancelled = false;

    const loadUsers = async () => {
      setSelectedUserId("");
      setFieldError("");
      setIsSubmitting(false);
      setIsLoadingUsers(true);

      try {
        const response = await axios.get(
          buildServiceUrl("connectorMaster", "keycloak/internal/users"),
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (!cancelled) {
          setAvailableUsers(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error("Error loading internal Keycloak users:", error);

        if (!cancelled) {
          setAvailableUsers([]);
          toast.error(
            t(
              "participant.userAssignment.loadError",
              "Could not load the Keycloak users.",
            ),
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoadingUsers(false);
        }
      }
    };

    loadUsers();

    return () => {
      cancelled = true;
    };
  }, [open, token]);

  const handleSubmit = async () => {
    if (!selectedUserId) {
      setFieldError(
        t(
          "participant.userAssignment.userRequired",
          "Select a Keycloak user before continuing.",
        ),
      );
      return;
    }

    const selectedUser = selectableUsers.find(
      (userOption) => userOption.value === selectedUserId,
    )?.user;

    if (!selectedUser?.id) {
      setFieldError(
        t(
          "participant.userAssignment.userInvalid",
          "The selected Keycloak user is invalid.",
        ),
      );
      return;
    }

    setIsSubmitting(true);
    setFieldError("");

    try {
      await updateInternalUser({
        userId: selectedUser.id,
        payload: {
          username: selectedUser.username,
          email: selectedUser.email,
          firstName: selectedUser.firstName,
          lastName: selectedUser.lastName,
          enabled: Boolean(selectedUser.enabled),
          attributes: {
            ...(selectedUser.attributes &&
            typeof selectedUser.attributes === "object"
              ? selectedUser.attributes
              : {}),
            organization: participantOrganization,
            participant_id: participantId,
          },
        },
      });

      toast.success(
        t(
          "toaster.userAssigned",
          "User linked to the participant successfully!",
        ),
      );
      await onSuccess?.();
      onClose?.();
    } catch (error) {
      toast.error(t("toaster.userError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={t("participant.userAssignment.title", "Add user")}
      maxWidth="sm"
      actions={
        <>
          <CustomButton
            variant="outlined"
            neutral
            onClick={onClose}
            disabled={isSubmitting}
            className="add-participant-user-dialog-action"
          >
            {t("profilePage.cancel")}
          </CustomButton>
          <CustomButton
            onClick={handleSubmit}
            disabled={
              isLoadingUsers ||
              isSubmitting ||
              !selectableUsers.length ||
              !participantId
            }
            className="add-participant-user-dialog-action"
          >
            {isSubmitting
              ? t("participant.userAssignment.saving", "Saving...")
              : t("participant.userAssignment.confirm", "Add user")}
          </CustomButton>
        </>
      }
    >
      <div className="add-participant-user-form">
        <Alert severity="info">
          {t(
            "participant.userAssignment.helper",
            "Select a Keycloak user to link to this participant. The user will inherit the participant organization.",
          )}
        </Alert>

        <div className="add-participant-user-summary">
          <span className="add-participant-user-summary-label">
            {t("participant.userAssignment.participantLabel", "Participant")}
          </span>
          <strong>{participantOrganization || "-"}</strong>
        </div>

        <div className="add-participant-user-field">
          <label htmlFor="participant-user-select">
            {t("participant.userAssignment.userLabel", "Keycloak user")}
          </label>

          {isLoadingUsers ? (
            <div className="add-participant-user-loading">
              <CircularProgress size={18} />
              <span>
                {t(
                  "participant.userAssignment.loading",
                  "Loading Keycloak users...",
                )}
              </span>
            </div>
          ) : (
            <Dropdown
              id="participant-user-select"
              ariaLabel={t(
                "participant.userAssignment.userLabel",
                "Keycloak user",
              )}
              variant="select"
              value={selectedUserId}
              onChange={(value) => {
                setSelectedUserId(value);
                if (fieldError) {
                  setFieldError("");
                }
              }}
              options={userOptions}
              placeholderKey="participant.userAssignment.userPlaceholder"
              disabled={!selectableUsers.length || isSubmitting}
              maxWidth={9999}
              sx={{ maxWidth: "100%" }}
            />
          )}

          {!isLoadingUsers && !selectableUsers.length ? (
            <p className="add-participant-user-hint">
              {t(
                "participant.userAssignment.noUsers",
                "There are no Keycloak users available to add to this participant.",
              )}
            </p>
          ) : null}

          {fieldError ? (
            <p className="add-participant-user-error">{fieldError}</p>
          ) : null}
        </div>
      </div>
    </AppDialog>
  );
}
